import React, { useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { RootState } from 'typesafe-actions';
import {
  ImageList as MuiImageList,
  ImageListItem as MuiImageListItem
} from '@material-ui/core';
import { CancelTwoTone as CancelTwoToneIcon, FullscreenTwoTone as FullscreenTwoToneIcon } from '@material-ui/icons';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import { Entry, Files, PortData } from '../services/types';
import { change24HourTo12 } from '../util';
import {
  closeDisplays,
  displayAlert,
  toggleDisplays,
  toggleFullSize,
  setShowImages
} from '../state/uiControl/uiControl.actions';
import { Display } from '../state/uiControl/uiControl.reducer';
import Paginator from './Pagination';
import Drawer from './Drawer';
import { useIsMobile } from '../hooks';
import { BlogEntry } from './ui-types';
import SimpleMenu, { MenuItem } from './SimpleMenu';
import { sortedImagesByKeys } from "./ImageBar";

const BASE_PATH = 'https://' + process.env.REACT_APP_CMS_DOMAIN + '/anchor/api/';

const useStyles = makeStyles(theme => ({
  entryContentContainer: {
    display: 'grid',
    gridTemplateRows: 'auto auto 1fr',
    height: '100%',
    gridGap: theme.spacing(1)
  },
  imageContainer: {
    display: 'flex',
    overflow: 'hidden',
    justifyContent: 'space-around',
    minHeight: theme.spacing(4),
  },
  menu: {
    paddingTop: '1rem',
  }
}));

const ImageList = withStyles({
  root: {
    flexWrap: 'nowrap',
    transform: 'translateZ(0)',
  },
})(MuiImageList);

const ImageListItem = withStyles({})(MuiImageListItem);

const ImageContainer = ({ children }: { children: React.ReactNode }) => {
  const classes = useStyles();
  const container = useRef<HTMLDivElement>(null);

  const isolateTouch: EventListenerOrEventListenerObject = (e) => {
    e.stopPropagation();
  };

  useEffect(() => {
    const containerNode = container.current;

    if (containerNode) {
      containerNode.addEventListener('touchstart', isolateTouch, { passive: true });
      containerNode.addEventListener('touchmove', isolateTouch, { passive: true });
      containerNode.addEventListener('touchend', isolateTouch, { passive: true });
      return () => {
        containerNode.removeEventListener('touchstart', isolateTouch);
        containerNode.removeEventListener('touchmove', isolateTouch);
        containerNode.removeEventListener('touchend', isolateTouch);
      };
    }
  }, []);

  return (
    <div className={classes.imageContainer} ref={container}>
      <ImageList cols={2.5} rowHeight={110}>
        {children}
      </ImageList>
    </div>
  );
};

const CloseIcon = withStyles({
  root: {
    backgroundColor: 'rgba(255,255,255,0.7)',
    borderRadius: '50%',
  }
})(CancelTwoToneIcon);

const FullscreenIcon = withStyles({
  root: {
    backgroundColor: 'rgba(255,255,255,0.7)',
    borderRadius: '50%',
  }
})(FullscreenTwoToneIcon);

export const EntryDrawer = ({
  displayAlert,
  selectedEntries,
  closeDisplays,
  setShowImages,
  selected,
  selectedVoyage,
  showing,
  toggleDisplays,
  toggleFullSize
}: EntryDrawerProps) => {
  const isMobile = useIsMobile();

  const getCurrIndex = (selectedEntry: Entry | PortData): number =>
    selectedEntries.findIndex((entry) => entry.id === selectedEntry.id);

  const setSelected = (index: number) => toggleDisplays(selectedEntries[index]);

  const showingPortOrEntry = isMobile && [Display.PORT, Display.ENTRY].some(display => display === showing);

  const actionButtonProps = {
    actionIcon: <FullscreenIcon />,
    actionLabel: 'open image gallery',
    onClickAction: selected?.files && !!Object.values(selected.files)?.[0]?.path
      ? () => toggleFullSize(BASE_PATH + Object.values(selected.files as Files)[0].path)
      : undefined,
  };

  const menuItems = selectedVoyage?.data?.video && [
    {text: 'Watch Video', onClick: () => window.open(selectedVoyage!.data.video!.link, '_blank')},
    {text: 'Download Video', onClick: () => window.open(selectedVoyage!.data.video!.downloadLink, '_blank')},
    {text: 'Copy Link to Share', onClick: () => {
      navigator.clipboard.writeText(selectedVoyage!.data.video!.link);
      displayAlert({message: 'Link copied!', severity: 'info'});
    }}
  ];

  return (
    <Drawer
      anchor='bottom'
      closeIcon={<CloseIcon />}
      open={showingPortOrEntry}
      onClose={closeDisplays}
      PaperProps={{
        variant: 'outlined',
        style: {
          borderRadius: "25px 25px 0 0",
        }
      }}
      {...actionButtonProps}
    >
      {selected && (
        <Paginator
          pages={selectedEntries}
          currentPage={getCurrIndex(selected)}
          setCurrentPage={setSelected}
          getKey={({ data }) => data.id}
          renderItem={({ data }: { data: Entry }) => (
            <EntryContent
              setShowImages={setShowImages}
              selected={data}
              menuItems={menuItems}
            />
          )}
        />)
      }
    </Drawer>
  );
};

interface EntryContentProps {
  menuItems?: MenuItem[];
  setShowImages: typeof setShowImages;
  selected: Entry;
}

export const EntryContent = ({
  menuItems,
  setShowImages,
  selected,
}: EntryContentProps) => {
  const isMobile = useIsMobile();
  const classes = useStyles();

  const entryToShow: BlogEntry = {
    title: "",
    content: "",
    time: "",
    latLong: ""
  };

  if (selected && Object.values(selected).length > 1) {
    entryToShow.title = selected.name;
    entryToShow.time = change24HourTo12(selected.data.time);

    if (selected.data.content) {
      entryToShow.content = selected.data.content;
    }

    if (selected.data.author) {
      entryToShow.content += "\n " + selected.data.author;
    }
    if (typeof selected.lat !== "undefined" && typeof selected.long !== "undefined") {
      entryToShow.latLong = "Latitude: " + selected.lat!.toFixed(2) +
        "° Longitude: " + selected.long!.toFixed(2) + "°";
    }
  }

  const images = selected && selected.files ? sortedImagesByKeys(selected.files) : [];

  return (
    <div className={classes.entryContentContainer}>
      <ImageContainer>
        {!!images && images.map((image: { path: string }) => (
          <ImageListItem key={image.path}>
            <img
              key={image.path}
              alt='preview'
              src={BASE_PATH + image.path}
              onClick={() => {
                if (!isMobile) {
                  setShowImages(true);
                }
              }}
            />
          </ImageListItem>
        ))}

      </ImageContainer>
      <div className="title-container">
        <div className="title" >{entryToShow.title}</div>
        <div className="latLong">{entryToShow.latLong}</div>
        <div className="time">{entryToShow.time} </div>
      </div>
      <div className="info-content">
        <div dangerouslySetInnerHTML={{ __html: entryToShow.content }} />
        {!!menuItems?.length &&
          <div className={classes.menu}>
            <SimpleMenu title='Video Journal' menuItems={menuItems}/>
          </div>}
      </div>
    </div>
  );


};

const mapStateToProps = (state: RootState, ownProps: any) => {
  const showing = state.uiControl.showing;
  return {
    selectedEntries: state.voyagesData.selectedVoyageEntries,
    selected: showing === Display.PORT ? state.uiControl.selectedPort :
      (showing === Display.ENTRY ? state.uiControl.selectedEntry : null),
    selectedVoyage: state.voyagesData.selectedVoyageData,
    showing: showing,
  };
};

const dispatchProps = {
  closeDisplays,
  displayAlert,
  setShowImages,
  toggleDisplays,
  toggleFullSize,
};

type EntryDrawerProps = ReturnType<typeof mapStateToProps> & typeof dispatchProps;

export default connect(
  mapStateToProps,
  dispatchProps
)(EntryDrawer);